import React from "react";

interface SettingsLeafProps {
	title: React.ReactNode;
	description?: React.ReactNode;
	/** Widen the content column (row-based editors that need one-line rows). */
	wide?: boolean;
	/** Uncapped width, for editor leaves with their own multi-column layout. */
	full?: boolean;
	children: React.ReactNode;
}

/**
 * Shared shell for a single settings leaf page: a constrained column with a
 * consistent heading, optional description and a vertically-spaced body.
 * New (Tailwind) styling — replaces the old Chakra/styled `section` wrappers
 * that the monolithic settings tabs used.
 */
export const SettingsLeaf: React.FC<SettingsLeafProps> = ({
	title,
	description,
	wide,
	full,
	children,
}) => (
	<div className={full ? "max-w-none" : wide ? "max-w-4xl" : "max-w-2xl"}>
		<h2 className="mb-1 text-xl font-semibold text-gray-900">{title}</h2>
		{description ? (
			<p className="mb-6 max-w-prose text-sm text-gray-500">{description}</p>
		) : (
			<div className="mb-6" />
		)}
		<div className="space-y-5">{children}</div>
	</div>
);

interface SettingsSubsectionProps {
	title: string;
	children: React.ReactNode;
}

/** A labelled sub-group within a leaf (e.g. the salary-code categories). */
export const SettingsSubsection: React.FC<SettingsSubsectionProps> = ({ title, children }) => (
	<div className="space-y-3">
		<h3 className="text-sm font-semibold text-gray-700">{title}</h3>
		{children}
	</div>
);
